import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { useCart } from "@/context/CartContext";
import { ShoppingBag, Coffee } from "lucide-react";
import { toast } from "sonner";

const Products = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        api.get("/products")
            .then((r) => {
                if (mounted) setProducts(r.data || []);
            })
            .catch((e) => {
                logError("Products load error", e);
                toast.error("Could not load roasts. Please try again.");
            })
            .finally(() => mounted && setLoading(false));
        return () => {
            mounted = false;
        };
    }, []);

    return (
        <section
            id="products"
            data-testid="products-section"
            className="relative py-24 md:py-32 bg-[#0B0E14]"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
                    <div>
                        <div className="bs-eyebrow">The Lineup</div>
                        <h2
                            data-testid="products-title"
                            className="font-anton text-4xl md:text-5xl lg:text-6xl uppercase text-white tracking-wide mt-4 leading-[1.05]"
                        >
                            Every Roast{" "}
                            <span className="text-[#004466]">Has a Story.</span>
                        </h2>
                    </div>
                    <p className="font-body text-slate-400 max-w-md leading-relaxed">
                        Freshly roasted to order. Choose your grind
                        and weight. Free shipping on every order.
                    </p>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                        {["s1", "s2", "s3", "s4"].map((k) => (
                            <div
                                key={k}
                                className="bs-card animate-pulse h-[620px]"
                            />
                        ))}
                    </div>
                ) : (
                    <>
                        <div
                            data-testid="products-grid-available"
                            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
                        >
                            {products
                                .filter((p) => !p.coming_soon)
                                .map((p, idx) => (
                                    <ProductCard key={p.id} product={p} delayIdx={idx} />
                                ))}
                        </div>

                        {products.some((p) => p.coming_soon) && (
                            <div
                                data-testid="coming-soon-section"
                                className="mt-24 md:mt-32"
                            >
                                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
                                    <div>
                                        <div className="bs-eyebrow text-[#F2C94C]">On The Horizon</div>
                                        <h3
                                            data-testid="coming-soon-title"
                                            className="font-anton text-3xl md:text-4xl lg:text-5xl uppercase text-white tracking-wide mt-4 leading-[1.05]"
                                        >
                                            Coming{" "}
                                            <span className="text-[#F2C94C]">Soon.</span>
                                        </h3>
                                    </div>
                                    <p className="font-body text-slate-400 max-w-md leading-relaxed">
                                        Frac packs for the break-room brewer and single-serve pods
                                        for the on-shift cup. Check back periodically — or drop
                                        your email below to be notified the day they drop.
                                    </p>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                                    {products
                                        .filter((p) => p.coming_soon)
                                        .map((p, idx) => (
                                            <ProductCard
                                                key={p.id}
                                                product={p}
                                                delayIdx={idx}
                                            />
                                        ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </section>
    );
};

const GRINDS = ["Whole Bean", "Ground"];

const ProductCard = ({ product, delayIdx }) => {
    const { addItem } = useCart();
    const isComingSoon = !!product.coming_soon;
    const variants = product.variants || [];
    const isSingleVariant = variants.length === 1;
    const [grind, setGrind] = useState(
        isSingleVariant ? variants[0].grind : "Whole Bean",
    );
    const size = isSingleVariant ? variants[0].size : "12 oz";

    const variant = useMemo(
        () =>
            variants.find((v) => v.grind === grind && v.size === size) || null,
        [variants, grind, size],
    );

    const handleAdd = () => {
        if (isComingSoon) return;
        if (!variant) {
            toast.error("Please pick a grind and size.");
            return;
        }
        addItem(product, variant, 1);
        const label = isSingleVariant ? size : `${grind}, ${size}`;
        toast.success(`${product.name} (${label}) added to cart`);
    };

    return (
        <article
            data-testid={`product-card-${product.id}`}
            className={`bs-card flex flex-col group bs-fade-up relative ${
                isComingSoon ? "opacity-95" : ""
            }`}
            style={{ animationDelay: `${0.05 * delayIdx}s` }}
        >
            {/* Image */}
            <div className="relative h-80 overflow-hidden bg-[#0B0E14]">
                <img
                    src={product.image}
                    alt={product.name}
                    className={`w-full h-full object-cover transition-transform duration-700 ${
                        isComingSoon ? "" : "group-hover:scale-105"
                    }`}
                />
                <div className="absolute top-4 left-4 bg-[#004466] px-3 py-1.5">
                    <span className="font-chivo text-[10px] tracking-[0.24em] uppercase text-white font-bold">
                        {product.badge}
                    </span>
                </div>
                <div className="absolute top-4 right-4 bg-black/70 backdrop-blur px-3 py-1.5 border border-[#1E293B]">
                    <span className="font-chivo text-[10px] tracking-[0.2em] uppercase text-white">
                        {product.roast}
                    </span>
                </div>
                {isComingSoon && (
                    <div
                        data-testid={`coming-soon-ribbon-${product.id}`}
                        className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent py-4 px-4"
                    >
                        <span className="font-anton text-lg md:text-xl tracking-wide uppercase text-[#F2C94C]">
                            Coming Soon
                        </span>
                    </div>
                )}
            </div>

            {/* Body */}
            <div className="p-6 flex flex-col flex-1">
                {product.tagline && (
                    <div className="font-chivo text-[10px] tracking-[0.28em] uppercase text-slate-500 mb-2">
                        {product.tagline}
                    </div>
                )}
                <h3 className="font-anton text-2xl uppercase text-white tracking-wide leading-tight">
                    {product.name}
                </h3>
                <p className="font-body text-sm text-slate-400 mt-3 leading-relaxed">
                    {product.description}
                </p>

                <div className="flex items-center gap-3 mt-4 text-xs font-body text-slate-500">
                    <Coffee className="w-3.5 h-3.5 text-[#004466]" />
                    <span>{product.origin}</span>
                </div>

                <div className="bs-stencil-divider my-5" />

                {isComingSoon ? (
                    <div className="mt-auto space-y-3">
                        <div className="font-chivo text-[10px] tracking-[0.24em] uppercase text-slate-500">
                            {variants[0]?.size} · {variants[0]?.grind}
                        </div>
                        <div
                            data-testid={`coming-soon-note-${product.id}`}
                            className="border border-[#F2C94C]/30 bg-[#F2C94C]/5 px-4 py-3 text-center"
                        >
                            <div className="font-anton text-base uppercase tracking-wide text-[#F2C94C]">
                                Coming Soon
                            </div>
                            <div className="font-body text-xs text-slate-400 mt-1">
                                Check back periodically —{" "}
                                <a
                                    href="#newsletter"
                                    data-testid={`notify-me-${product.id}`}
                                    className="underline hover:text-white transition-colors"
                                >
                                    join the list to be notified
                                </a>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Variant selectors */}
                        <div className="space-y-3 mb-5">
                            {isSingleVariant ? (
                                <div className="font-chivo text-[10px] tracking-[0.24em] uppercase text-slate-500">
                                    {size}
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <div className="font-chivo text-[10px] tracking-[0.24em] uppercase text-slate-500 mb-2">
                                            Grind
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            {GRINDS.map((g) => (
                                                <button
                                                    key={g}
                                                    data-testid={`${product.id}-grind-${g.toLowerCase().replace(/\s+/g, "-")}`}
                                                    onClick={() => setGrind(g)}
                                                    className={`py-2 text-[11px] font-chivo tracking-[0.14em] uppercase border transition-colors ${
                                                        grind === g
                                                            ? "bg-[#004466] border-[#004466] text-white"
                                                            : "border-[#1E293B] text-slate-300 hover:border-[#004466]"
                                                    }`}
                                                >
                                                    {g}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="font-chivo text-[10px] tracking-[0.24em] uppercase text-slate-500">
                                        12 oz bag
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="flex items-center justify-between mt-auto">
                            <div>
                                <div className="font-chivo text-[10px] tracking-[0.24em] uppercase text-slate-500">
                                    Price
                                </div>
                                <div
                                    data-testid={`${product.id}-price`}
                                    className="font-anton text-3xl text-[#ffffff]"
                                >
                                    ${variant ? variant.price.toFixed(2) : "—"}
                                </div>
                            </div>
                            <button
                                data-testid={`add-to-cart-${product.id}`}
                                onClick={handleAdd}
                                disabled={!variant}
                                className="btn-primary !py-3 !px-5 disabled:opacity-50"
                            >
                                <ShoppingBag className="w-4 h-4" />
                                Add
                            </button>
                        </div>
                    </>
                )}
            </div>
        </article>
    );
};

export default Products;
